import { DateTime } from 'luxon';
import { createApiInstance } from './handler';
import { PRODUCT_CLASSIFICATION, REPORT_TYPE } from '../utils/constants';

let wmPartial = false;
let selfPacedPartial = false;
let selfCompetenciesPartial = false;

export const getAssessmentAssignmentSummary = async (auth) => {
  try {
    const response = await createApiInstance(auth).get('/assessment-assignments?limit=100');
    if (response.data?.data) {
      return response.data.data;
    }
    console.log(response); // TODO Some real logging
  } catch (reason) {
    console.log(reason); // TODO Some real logging
  }
  return [];
};

const isSelfCompetency = (assessmentCode) => {
  return assessmentCode === 'C';
};

const getAssessmentQuestions = async (auth, id) => {
  try {
    const response = await createApiInstance(auth).get(
      `/assessment-assignments/${id}/questions?limit=500&include_answers=1`
    );
    if (response.data?.data) {
      return {
        assessmentId: id,
        questions: response.data.data,
      };
    }
    console.log(response); // TODO Some real logging
  } catch (reason) {
    console.log(reason); // TODO Some real logging
  }
  return [];
};

const submitAnswer = async (auth, questionId, answer) => {
  try {
    await createApiInstance(auth).post(`/assessment-assignments/${questionId}/question-answers`, answer);
  } catch (reason) {
    console.log(reason); // TODO Some real logging
  }
};

export const getAssessmentAssignments = async (auth) => {
  const workingMemoryAssignments = [];
  const selfPacedAssignments = [];
  const selfCompetenciesAssignments = [];
  const raterCompetenciesAssignments = [];
  const assignedAssessments = {
    selfPaced: false,
    workingMemory: false,
    selfCompetencies: false,
    raterCompetencies: false,
  };
  let assigned = DateTime.now().toLocaleString(DateTime.DATETIME_SHORT);
  let expires = null;
  wmPartial = false;
  selfPacedPartial = false;
  selfCompetenciesPartial = false;

  try {
    const assignments = await getAssessmentAssignmentSummary(auth);
    if (assignments && assignments.length > 0) {
      const assessments = {};
      const questionResults = [];
      assignments.forEach((assignment) => {
        const createdAt = assignment.created_at;
        const expiresAt = assignment.expires_at;
        const assessmentName = assignment.assessment.name;
        const assessmentCode = assignment.assessment.code;
        if (createdAt) {
          const assignedTime = DateTime.fromISO(createdAt).toLocal();
          assigned = assignedTime.toLocaleString(DateTime.DATE_MED);
        }
        if (expiresAt) {
          expires = expiresAt;
        }
        let assessmentTitle = assignment.title ? assignment.title : assessmentName;
        let isARaterAssessment = false;
        const assessmentType = assignment.assessment_assignments_page_type;
        switch (assessmentType) {
          case 'working-memory':
            assignedAssessments.workingMemory = true;
            break;
          case 'self-paced':
            assignedAssessments.selfPaced = true;
            break;
          case 'peer-experience':
            if (isSelfCompetency(assessmentCode)) {
              assignedAssessments.selfCompetencies = true;
              assessmentTitle = 'Competencies';
            } else {
              assignedAssessments.raterCompetencies = true;
              isARaterAssessment = true;
              if (assessmentTitle.indexOf(':') > 0) {
                assessmentTitle = `Competencies - ${assessmentTitle.split(':')[1].trim()}`;
              }
            }
            break;
        }
        if (!assignment.submitted_by_target_user_at || isARaterAssessment) {
          assessments[assignment.id] = {
            id: assignment.id,
            title: assessmentTitle,
            name: assessmentName,
            code: assessmentCode,
            instructions: assignment.assessment.instructions,
            type: assessmentType,
            assigned,
            expires,
            started: assignment.started_by_target_user_at,
            submitted: assignment.submitted_by_target_user_at, // Specifically for rater assessments
          };
          questionResults.push(getAssessmentQuestions(auth, assignment.id));
        } else if (assessmentType === 'working-memory') {
          wmPartial = true;
        } else if (assessmentType === 'peer-experience') {
          if (isSelfCompetency(assessmentCode)) {
            selfCompetenciesPartial = true;
          }
        } else {
          selfPacedPartial = true;
        }
      });

      const responses = await Promise.all(questionResults);
      responses.forEach((response) => {
        const assessment = assessments[response.assessmentId];
        assessment.questions = response.questions;
        for (let q = 0; q < assessment.questions.length; q += 1) {
          const question = assessment.questions[q];
          if (question.answer?.anchoring_item_id) {
            question.answer = question.answer.anchoring_item_id;
          } else if (question.answer?.value) {
            question.answer = question.answer.value;
          } else {
            question.answer = undefined;
          }
        }
        if (assessment.type === 'working-memory') {
          workingMemoryAssignments.push(assessment);
        } else if (assessment.type === 'self-paced') {
          selfPacedAssignments.push(assessment);
        } else if (assessment.type === 'peer-experience') {
          if (isSelfCompetency(assessment.code)) {
            selfCompetenciesAssignments.push(assessment);
          } else {
            raterCompetenciesAssignments.push(assessment);
          }
        }
      });
    }
  } catch (reason) {
    console.log(reason); // TODO Some real logging
  }

  return {
    assigned,
    assignedAssessments,
    workingMemory: workingMemoryAssignments,
    selfPaced: selfPacedAssignments,
    selfCompetencies: selfCompetenciesAssignments,
    raterCompetencies: raterCompetenciesAssignments,
  };
};

export const markAssessmentStarted = async (auth, assessmentId) => {
  try {
    const dt = DateTime.local().setZone('utc').startOf('second');
    const assessmentStart = dt.toISO({
      suppressMilliseconds: true,
    });
    await createApiInstance(auth).put(`/assessment-assignments/${assessmentId}/starting`, {
      datetime: assessmentStart,
    });
  } catch (reason) {
    console.log(reason);
  }
};

export const markAssessmentSubmitted = async (auth, assessmentId) => {
  try {
    const dt = DateTime.local().setZone('utc').startOf('second');
    const assessmentSubmitted = dt.toISO({
      suppressMilliseconds: true,
    });
    await createApiInstance(auth).put(`/assessment-assignments/${assessmentId}/submittal`, {
      datetime: assessmentSubmitted,
    });
  } catch (reason) {
    console.log(reason);
  }
};

export const submitAnswers = async (auth, questions, assessmentCompleted) => {
  if (questions && questions.length) {
    const questionResults = [];
    const { assessmentId } = questions[0];
    questions.forEach((question) => {
      const answer = {
        question_id: Number(question.question.id),
      };
      if (question.question.anchoring?.items && question.question.anchoring.items.length > 0) {
        answer.anchoring_id = Number(question.question.anchoring.id);
        answer.anchoring_item_id = Number(question.answer);
      } else {
        if (question.question.anchoring?.type === 'text') {
          answer.value = question.answer;
        } else {
          answer.value = Number(question.answer);
        }
      }
      questionResults.push(submitAnswer(auth, assessmentId, answer));
    });
    await Promise.all(questionResults);
    if (assessmentCompleted) {
      await markAssessmentSubmitted(auth, assessmentId);
    }
  }
};

export const getAssessmentCutoffs = async (auth, assessmentId) => {
  const assessmentCutoffs = {};
  const results = await createApiInstance(auth, 'enduser').get(`assessment-cutoffs?assessment_ids[0]=${assessmentId}`);
  if (results.data?.data && results.data.data.length > 0) {
    assessmentCutoffs.breakpoints = {
      breaks: [],
      scale: 0,
    };
    assessmentCutoffs.summaries = {};
    assessmentCutoffs.summaries[REPORT_TYPE.coaching] = [];
    assessmentCutoffs.summaries[REPORT_TYPE.development] = [];
    assessmentCutoffs.summaries[REPORT_TYPE.staffing] = [];
    results.data.data.forEach((cutoff) => {
      if (cutoff.max > assessmentCutoffs.breakpoints.scale) {
        assessmentCutoffs.breakpoints.scale = cutoff.max;
      }
      if (cutoff.min > 0) {
        assessmentCutoffs.breakpoints.breaks.push(cutoff.min);
      }
      for (let c = 0; c < cutoff.comments.length; c++) {
        assessmentCutoffs.summaries[cutoff.comments[c].type].push({
          breakpoint: cutoff.min,
          text: cutoff.comments[c].body,
        });
      }
    });
    assessmentCutoffs.breakpoints.breaks.sort().reverse();

    Object.keys(assessmentCutoffs.summaries).forEach((key) => {
      assessmentCutoffs.summaries[key].sort((a, b) => {
        if (a.breakpoint > b.breakpoint) {
          return -1;
        } else if (a.breakpoint < b.breakpoint) {
          return 1;
        }

        return 0;
      });
    });
  }

  return assessmentCutoffs;
};

export const getCodesCutoffs = async (auth, assessmentId, questionCodes) => {
  const questionCutoffs = {};
  const strQuestionCodes = questionCodes.join(',');
  const results = await createApiInstance(auth, 'enduser').get(
    `assessment-question-cutoffs?question_codes=${strQuestionCodes}&assessment_id=${assessmentId}`
  );
  if (results.data?.question_cutoffs?.data) {
    results.data.question_cutoffs.data.forEach((cutoffData) => {
      if (cutoffData.cutoffs) {
        cutoffData.cutoffs.forEach((cutoff) => {
          if (!questionCutoffs[cutoff.code]) {
            questionCutoffs[cutoff.code] = {};
            questionCutoffs[cutoff.code].comments = {};
            questionCutoffs[cutoff.code].comments[REPORT_TYPE.coaching] = [];
            questionCutoffs[cutoff.code].comments[REPORT_TYPE.development] = [];
            questionCutoffs[cutoff.code].comments[REPORT_TYPE.staffing] = [];
          }

          const cutoffDetail = questionCutoffs[cutoff.code];
          cutoffDetail.title = cutoff.name;
          const breakpoint = cutoff.min;
          if (cutoff.comments) {
            if (!cutoffDetail.comments) {
              cutoffDetail.comments = [];
            }
            cutoff.comments.forEach((comment) => {
              const commentEntry = {
                breakpoint: breakpoint,
                comment: comment.body,
              };
              cutoffDetail.comments[comment.type].push(commentEntry);
            });
          }
          if (cutoff.tips) {
            if (!cutoffDetail.tips) {
              cutoffDetail.tips = [];
            }
            cutoff.tips.forEach((tip) => {
              const tipsEntry = {
                order: tip.order,
                text: tip.body,
                title: tip.title,
              };
              cutoffDetail.tips.push(tipsEntry);
            });
          }
        });
      }
    });

    Object.keys(questionCutoffs).forEach((key) => {
      const cutoff = questionCutoffs[key];
      const commentKeys = Object.keys(cutoff.comments);
      if (commentKeys.length > 0) {
        commentKeys.forEach((key) => {
          cutoff.comments[key].sort((a, b) => {
            if (a.breakpoint > b.breakpoint) {
              return -1;
            } else if (a.breakpoint < b.breakpoint) {
              return 1;
            }
          });
        });
      }
      if (cutoff.tips) {
        cutoff.tips.sort((a, b) => {
          if (b.order > a.order) {
            return -1;
          } else if (b.order < a.order) {
            return 1;
          }

          return 0;
        });
      }
    });
  }
  return questionCutoffs;
};

export const getAssessmentStatus = (assessment) => {
  const status = {};
  switch (assessment.type) {
    case 'working-memory':
      status.started = wmPartial || assessment.started;
      break;
    case 'peer-experience':
      if (isSelfCompetency(assessment.code)) {
        status.started = selfCompetenciesPartial || assessment.started;
      } else {
        status.started = assessment.started;
      }
      break;
    default:
      status.started = selfPacedPartial || assessment.started;
  }
  status.submitted = !!assessment.submitted;
  if (assessment.expires) {
    const expiresTime = DateTime.fromISO(assessment.expires).toLocal();
    if (expiresTime < DateTime.now()) {
      status.expired = true;
    }
  }
  return status;
};

export const launch = async (auth, launchData) => {
  const response = await createApiInstance(auth, 'client').post(`/assessment-assignment/launch`, launchData);
  if (response.data) {
    return response.data;
  }

  return response;
};

export const launchCSV = async (auth, launchData) => {
  const formData = new FormData();
  formData.append(
    'product_classification',
    launchData.select ? PRODUCT_CLASSIFICATION.select : PRODUCT_CLASSIFICATION.develop
  );
  formData.append('csv', launchData.csv);
  const response = await createApiInstance(auth, 'client').post(`/assessment-assignment/launch-csv`, formData, {
    responseType: 'blob',
  });
  const now = DateTime.now();
  const filename = `invitations-${now.toFormat('yyyy-LL-dd_HH-mm-ss')}.csv`;

  const file = new Blob([response.data], { type: 'text/csv' });
  const fileURL = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = filename;
  link.click();

  const launchStatistics = JSON.parse(response.headers['x-launch-statistics']);
  launchStatistics.filename = filename;
  return launchStatistics;
};
